import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Download, AlertCircle, Image, RotateCcw, Loader2,
  CheckCircle2, XCircle, Plus, Trash2, Info, AlertTriangle
} from 'lucide-react';

type OutputFormat = 'jpg' | 'png' | 'webp';

interface FileEntry {
  id: string;
  file: File;
  preview: string | null;
  isHeic: boolean;
  resultUrl: string | null;
  resultSize: number;
  status: 'pending' | 'loading' | 'ready' | 'converting' | 'done' | 'error';
  error: string | null;
}

const HeicToJpg: React.FC = () => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('jpg');
  const [quality, setQuality] = useState(92);
  const [stripMetadata, setStripMetadata] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [browserSupported, setBrowserSupported] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const checkIsHeic = async (file: File): Promise<boolean> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer.slice(0, 12));
    // HEIC/HEIF files have "ftyp" at offset 4, followed by heic, heix, hevc, mif1, etc.
    if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
      return ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand);
    }
    // Also accept based on extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext === 'heic' || ext === 'heif';
  };

  const handleFileSelect = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setError(null);

    const newEntries: FileEntry[] = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const f = selectedFiles[i];
      const id = `${Date.now()}-${i}`;
      const isHeic = await checkIsHeic(f);

      newEntries.push({
        id, file: f, preview: null, isHeic,
        resultUrl: null, resultSize: 0,
        status: 'loading', error: null,
      });
    }

    setFiles(prev => [...prev, ...newEntries]);

    // Try to load previews
    for (const entry of newEntries) {
      const url = URL.createObjectURL(entry.file);
      const img = new window.Image();
      img.onload = () => {
        if (browserSupported === null) setBrowserSupported(true);
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, preview: url, status: 'ready' } : f
        ));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        if (entry.isHeic) {
          setBrowserSupported(false);
          setFiles(prev => prev.map(f =>
            f.id === entry.id ? {
              ...f, status: 'error',
              error: 'Your browser cannot decode HEIC files. Try Chrome 126+, Edge, or Safari.'
            } : f
          ));
        } else {
          setFiles(prev => prev.map(f =>
            f.id === entry.id ? { ...f, status: 'error', error: 'Failed to load image' } : f
          ));
        }
      };
      img.src = url;
    }
  }, [browserSupported]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const convertFile = useCallback((entry: FileEntry) => {
    if (!entry.preview) return;
    setFiles(prev => prev.map(f =>
      f.id === entry.id ? { ...f, status: 'converting', error: null } : f
    ));

    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // For JPG, fill with white background (no transparency)
      if (outputFormat === 'jpg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);

      const mimeMap: Record<OutputFormat, string> = {
        jpg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
      };

      const q = outputFormat === 'png' ? undefined : quality / 100;

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resultUrl = URL.createObjectURL(blob);
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, resultUrl, resultSize: blob.size, status: 'done' } : f
            ));
          } else {
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, status: 'error', error: 'Conversion failed' } : f
            ));
          }
        },
        mimeMap[outputFormat],
        q
      );
    };
    img.onerror = () => {
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'error', error: 'Failed to process image' } : f
      ));
    };
    img.src = entry.preview;
  }, [outputFormat, quality]);

  const convertAll = useCallback(() => {
    files.filter(f => f.status === 'ready').forEach(entry => convertFile(entry));
  }, [files, convertFile]);

  const downloadFile = (entry: FileEntry) => {
    if (!entry.resultUrl) return;
    const link = document.createElement('a');
    link.href = entry.resultUrl;
    const name = entry.file.name.replace(/\.[^.]+$/, '');
    link.download = `${name}.${outputFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    files.filter(f => f.status === 'done').forEach(entry => downloadFile(entry));
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry) {
        if (entry.preview) URL.revokeObjectURL(entry.preview);
        if (entry.resultUrl) URL.revokeObjectURL(entry.resultUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const reset = () => {
    files.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
      if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
    });
    setFiles([]);
    setError(null);
    setBrowserSupported(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const readyCount = files.filter(f => f.status === 'ready').length;
  const doneCount = files.filter(f => f.status === 'done').length;

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-pink-100 rounded-full">
            <Image className="w-8 h-8 text-pink-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">HEIC to JPG Converter</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Convert HEIC/HEIF images from your iPhone or iPad to JPG, PNG, or WebP. All processing is done in your browser.
        </p>
      </div>

      {/* Browser support warning */}
      {browserSupported === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">HEIC not supported by your browser</p>
            <p className="text-sm text-amber-700 mt-1">
              Your browser cannot decode HEIC files natively. Please try:
            </p>
            <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
              <li>Google Chrome 126+ (recommended)</li>
              <li>Microsoft Edge (latest)</li>
              <li>Safari on macOS</li>
            </ul>
          </div>
        </div>
      )}

      {/* Upload */}
      {files.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-pink-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop HEIC/HEIF images here or click to select
          </h3>
          <p className="text-gray-500 text-sm">Select multiple files for batch conversion</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".heic,.heif,image/*"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Options */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
                <div className="flex gap-2">
                  {([['jpg', 'JPG'], ['png', 'PNG'], ['webp', 'WebP']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setOutputFormat(val)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        outputFormat === val
                          ? 'bg-pink-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:border-pink-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {outputFormat !== 'png' && (
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Quality</label>
                    <span className="text-lg font-bold text-pink-600">{quality}%</span>
                  </div>
                  <input
                    type="range" min="1" max="100" value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-600"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Smallest</span>
                    <span>Best quality</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={stripMetadata}
                  onChange={(e) => setStripMetadata(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
              </label>
              <span className="text-sm text-gray-700">Strip EXIF metadata (canvas re-draw removes all metadata)</span>
            </div>
          </div>

          {/* File list */}
          <div className="space-y-3">
            {files.map(entry => (
              <div key={entry.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                {/* Thumbnail */}
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                  {entry.preview ? (
                    <img src={entry.preview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-300" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{entry.file.name}</p>
                    {entry.isHeic && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-pink-100 text-pink-700">HEIC</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{formatSize(entry.file.size)}</span>
                    {entry.status === 'loading' && (
                      <span className="text-blue-600 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                      </span>
                    )}
                    {entry.status === 'converting' && (
                      <span className="text-blue-600 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Converting...
                      </span>
                    )}
                    {entry.status === 'done' && (
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {outputFormat.toUpperCase()}: {formatSize(entry.resultSize)}
                        {entry.resultSize < entry.file.size
                          ? ` (${Math.round((1 - entry.resultSize / entry.file.size) * 100)}% smaller)`
                          : ''}
                      </span>
                    )}
                    {entry.status === 'error' && (
                      <span className="text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> {entry.error}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {entry.status === 'done' && (
                    <button
                      onClick={() => downloadFile(entry)}
                      className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => removeFile(entry.id)}
                    className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add more */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-pink-300 hover:text-pink-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add more files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".heic,.heif,image/*"
            multiple
            onChange={(e) => { handleFileSelect(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; }}
            className="hidden"
          />

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {readyCount > 0 && (
              <button
                onClick={convertAll}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-pink-600 text-white font-medium rounded-xl hover:bg-pink-700 transition-colors"
              >
                <Image className="w-5 h-5" />
                Convert {readyCount > 1 ? `All (${readyCount})` : ''} to {outputFormat.toUpperCase()}
              </button>
            )}
            {doneCount > 1 && (
              <button
                onClick={downloadAll}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download All ({doneCount})
              </button>
            )}
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
    </div>
  );
};

export default HeicToJpg;
